import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../context';
import type { MediaDetail, ChildTag, ChildItem } from '../types';

export function useMediaDetail(mediaId: string | undefined) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['media', mediaId],
    queryFn: () => apiClient.get<{ media: MediaDetail }>(`/api/media/${mediaId}`),
    enabled: !!mediaId,
  });
}

export function useMediaChildTags(mediaId: string | undefined, enabled: boolean) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['media', mediaId, 'children'],
    queryFn: () => apiClient.get<{ children: ChildTag[] }>(`/api/media/${mediaId}/children`),
    enabled: !!mediaId && enabled,
  });
}

export function useAvailableChildren(familyId: string | undefined, enabled: boolean) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['children', familyId],
    queryFn: () => apiClient.get<{ children: ChildItem[] }>(`/api/families/${familyId}/children`),
    enabled: !!familyId && enabled,
  });
}

export function useUpdateMediaChildTags(mediaId: string | undefined, familyId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (childIds: string[]) =>
      apiClient.put<{ children: ChildTag[] }>(`/api/media/${mediaId}/children`, { childIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', mediaId, 'children'] });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: ['children', familyId] });
        queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
      }
    },
  });
}

export function useUpdateMedia(mediaId: string | undefined, familyId?: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: { caption?: string | null; takenAt?: number | null; rotation?: number; accessGroupId?: string | null }) =>
      apiClient.patch<{ media: MediaDetail }>(`/api/media/${mediaId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', mediaId] });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
      }
    },
  });
}

export function useDeleteMedia(mediaId: string | undefined, familyId?: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.delete(`/api/media/${mediaId}`),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
      }
    },
  });
}
